import { emailRegex, passwordRegex } from "./auth_validation";

export type FieldErrors = Partial<
	Record<"name" | "email" | "password" | "confirmPassword" | "code", string>
>;

export type AuthStep =
	| "login"
	| "send-code"
	| "register"
	| "send-reset"
	| "verify-reset"
	| "reset-password";

const REQUIRED = "auth-page.errors.is-required";

export const validateLogin = ({
	email,
	password,
}: {
	email: string;
	password: string;
}): FieldErrors => {
	const errors: FieldErrors = {};
	if (!email.trim()) errors.email = REQUIRED;
	if (!password.trim()) errors.password = REQUIRED;
	return errors;
};

export const validateEmail = (email: string): FieldErrors => {
	if (!email) return { email: REQUIRED };
	if (!emailRegex.test(email)) return { email: "auth-page.errors.uncorrect-email" };
	return {};
};

export const validateNewPassword = ({
	password,
	confirmPassword,
}: {
	password: string;
	confirmPassword: string;
}): FieldErrors => {
	const errors: FieldErrors = {};
	if (!password) errors.password = REQUIRED;
	else if (!passwordRegex.test(password))
		errors.password = "auth-page.errors.password-verification";

	if (!confirmPassword) errors.confirmPassword = REQUIRED;
	else if (password && confirmPassword !== password) {
		errors.confirmPassword = "auth-page.errors.passwords-not-equal";
	}
	return errors;
};

export const validateRegistration = (d: {
	name: string;
	email: string;
	password: string;
	confirmPassword: string;
}): FieldErrors => {
	const errors: FieldErrors = {};
	const name = d.name.trim();
	if (!name) errors.name = REQUIRED;
	else if (name.length < 2) errors.name = "auth-page.errors.fewer-characters";

	return {
		...errors,
		...validateEmail(d.email),
		...validateNewPassword({ password: d.password, confirmPassword: d.confirmPassword }),
	};
};

export const validateCode = (code: string): FieldErrors => {
	const value = code.trim();
	if (!value) return { code: REQUIRED };
	if (value.length !== 6) return { code: "auth-page.auth-code.errors.code-verification" };
	return {};
};

const CODE_ERRORS: Record<number, string> = {
	410: "auth-page.auth-code.errors.410",
	422: "auth-page.auth-code.errors.422",
};

/** The same answers the /auth page gives, keyed by the step that failed. */
export const authErrorKey = (
	step: AuthStep,
	status: number | undefined,
): { field: "email" | "code" | null; key: string } => {
	const code = (status && CODE_ERRORS[status]) || undefined;
	switch (step) {
		case "login":
			return {
				field: null,
				key:
					status === 403
						? "auth-page.errors.email-or-password-error"
						: "auth-page.errors.login-error",
			};
		case "send-code":
			return status === 409
				? { field: "email", key: "auth-page.errors.409" }
				: { field: null, key: "auth-page.errors.registr-error" };
		case "register":
			return {
				field: "code",
				key: status === 409 ? "auth-page.errors.409" : code || "auth-page.errors.registr-error",
			};
		case "send-reset":
			return status === 404
				? { field: "email", key: "auth-page.errors.user-not-found" }
				: { field: null, key: "auth-page.errors.recover-error" };
		case "verify-reset":
			return { field: "code", key: code || "auth-page.errors.recover-error" };
		case "reset-password":
			return { field: null, key: "auth-page.new-password.error" };
	}
};
